// ─────────────────────────────────────────────────────────────────────────────
// useAppMode + useMetricPreferences
// Detects the current CrewZ operating mode and provides mode-scoped
// screen-layout preferences. Mode hierarchy (highest priority first):
//
//   Mode 1 — Full Hardware  : CrewZ unit detected via BLE
//   Mode 3 — Ergo           : PM5 detected OR Ergo boat selected
//   Mode 4 — NK EmPower     : reserved (detection not yet implemented)
//   Mode 5 — XBoat          : reserved (detection not yet implemented)
//   Mode 2 — Phone Only     : default when nothing else is detected
//
// Storage: localStorage `metric_preferences_by_mode` keyed `modeN`:
//   { mode1: {slot: metricId, ...}, mode2: {...}, ... }
// A one-shot migration moves the legacy flat `metric_preferences` dict into
// ALL five mode buckets on first load so existing users keep their layout.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { crewzBleService } from '../services/crewzBle';
import { getOarlockDriver } from './useOarlockBleConnection';

export const MODES = {
  FULL_HARDWARE: 1,
  PHONE_ONLY: 2,
  ERGO: 3,
  NK_EMPOWER: 4,
  XBOAT: 5,
};

export const MODE_LABELS = {
  1: 'CrewZ',
  2: 'Phone Only',
  3: 'Ergometer',
  4: 'NK EmPower',     // RESERVED — NK ecosystem is closed (ADV_SCAN_IND, non-connectable). Never reached.
  5: 'XBoat XPod',
};

const LEGACY_KEY = 'metric_preferences';
const STORAGE_KEY = 'metric_preferences_by_mode';

/**
 * Detect the active app mode based on connected hardware + boat selection.
 *
 * Priority hierarchy (post-NK-pivot, 2026-06):
 *
 *   Mode 5 — XBoat XPod         (XBoat driver `connected === true`)
 *   Mode 1 — CrewZ              (CrewZ BLE case connected)
 *   Mode 3 — Ergometer          (PM5 connected OR boat class = c2_erg)
 *   Mode 2 — Phone Only         (default fallback)
 *
 * Mode 4 (NK EmPower) is RESERVED — NK's oarlock + SpeedCoach both advertise
 * ADV_SCAN_IND (non-connectable) and reject third-party Centrals. The NK
 * driver still exists in the codebase as a parser-only stub for future use.
 *
 * The mode badge re-evaluates on every status change emitted by the
 * underlying drivers so the UI always reflects live state. On disconnect
 * mid-session the badge seamlessly falls back to Mode 2.
 */
export function useAppMode({ pm5Device, boatClass } = {}) {
  // Track CrewZ connection reactively
  const [crewzConnected, setCrewzConnected] = useState(() => crewzBleService.isConnected());
  useEffect(() => {
    const onConnected = () => setCrewzConnected(true);
    const onDisconnected = () => setCrewzConnected(false);
    crewzBleService.on('connected', onConnected);
    crewzBleService.on('disconnected', onDisconnected);
    return () => {
      crewzBleService.off('connected', onConnected);
      crewzBleService.off('disconnected', onDisconnected);
    };
  }, []);

  // Track XBoat XPod driver connection — drives Mode 5 detection.
  const [xbConnected, setXbConnected] = useState(() => {
    try { return !!getOarlockDriver('xboat')?.getStatus?.()?.connected; }
    catch { return false; }
  });
  useEffect(() => {
    const driver = getOarlockDriver('xboat');
    if (!driver?.subscribeStatus) return undefined;
    return driver.subscribeStatus((s) => setXbConnected(!!s?.connected));
  }, []);

  const mode = useMemo(() => {
    if (xbConnected)    return MODES.XBOAT;
    if (crewzConnected) return MODES.FULL_HARDWARE;
    if (pm5Device || boatClass === 'c2_erg') return MODES.ERGO;
    return MODES.PHONE_ONLY;
  }, [xbConnected, crewzConnected, pm5Device, boatClass]);

  return { mode, label: MODE_LABELS[mode] || 'Unknown' };
}

/**
 * One-shot migration: if the user already has a legacy flat
 * `metric_preferences` dict, clone it into every mode bucket so no existing
 * layout is lost. Runs once and records completion via a marker in the new
 * storage shape (presence of any key means migration has occurred).
 */
function migrateLegacyPreferences() {
  try {
    const existing = localStorage.getItem(STORAGE_KEY);
    if (existing) return; // already in the new shape
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (!legacy) return;
    const legacyParsed = JSON.parse(legacy);
    if (!legacyParsed || typeof legacyParsed !== 'object') return;
    const byMode = {};
    Object.values(MODES).forEach(n => { byMode[`mode${n}`] = { ...legacyParsed }; });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(byMode));
    // Keep the legacy key for one release in case any other component still reads it.
  } catch (e) {
    console.warn('[useMetricPreferences] legacy migration failed:', e?.message || e);
  }
}

const readAllPrefs = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

/**
 * Mode-scoped metric preferences. Returns the current mode's slot dict plus
 * a setter that persists changes to the appropriate mode bucket.
 *
 * The bucket key is composed from up to three dimensions so a rower's layout
 * is remembered separately for each combination:
 *   • mode             — app mode (1=Full Hardware, 2=Phone, 3=Ergo, 4=NK, 5=XB)
 *   • targetMode       — 'standard' | 'race'  (workout intent on the Monitor)
 *   • targetDistance   — meters (only meaningful for race/target workouts)
 *
 * Bucket key shape: `mode{N}` (legacy) OR `mode{N}__{targetMode}__{distance}`.
 * Lookup is hierarchical: it tries the most-specific bucket first, then falls
 * back to mode-only so existing layouts keep working when a rower enters Race
 * mode for the first time.
 */
export function useMetricPreferences(mode, subMode = {}) {
  // One-shot migration on mount
  const migratedRef = useRef(false);
  if (!migratedRef.current) {
    migrateLegacyPreferences();
    migratedRef.current = true;
  }

  const [allPrefs, setAllPrefs] = useState(readAllPrefs);

  // Keep in sync if another tab writes to the same key.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setAllPrefs(readAllPrefs());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Build cascade of bucket keys (most-specific first).
  const bucketKeys = useMemo(() => {
    const base = `mode${mode}`;
    const keys = [];
    const { targetMode, targetDistance } = subMode || {};
    if (targetMode && targetDistance) {
      keys.push(`${base}__${targetMode}__${targetDistance}`);
    }
    if (targetMode) {
      keys.push(`${base}__${targetMode}`);
    }
    keys.push(base);   // fallback
    return keys;
  }, [mode, subMode?.targetMode, subMode?.targetDistance]);

  // Hierarchical merge: start from the broadest, overlay each more-specific.
  const preferences = useMemo(() => {
    return [...bucketKeys].reverse().reduce((acc, key) => ({ ...acc, ...(allPrefs[key] || {}) }), {});
  }, [allPrefs, bucketKeys]);

  // Writes ALWAYS go to the most-specific bucket so the rower's tweak only
  // affects this exact (mode, targetMode, distance) combo.
  const writeKey = bucketKeys[0];

  // Cross-device sync: on mount, hydrate from backend (if user is signed in)
  // so a rower who set their tiles on their phone sees the same layout on a
  // fresh tablet login. On each setSlot, we also fire-and-forget a save to the
  // backend so preferences follow the user across devices.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    const api = process.env.REACT_APP_BACKEND_URL;
    if (!api) return;
    (async () => {
      try {
        const res = await fetch(`${api}/api/users/preferences`, { credentials: 'include' });
        if (!res.ok) return; // Not signed in yet — localStorage stays authoritative
        const remote = await res.json();
        const v2 = remote?.monitor_metrics_v2;
        if (!v2 || typeof v2 !== 'object' || !Object.keys(v2).length) return;
        // Merge remote INTO local: local (unsaved) wins on conflict per-slot
        // so a rower's in-session tweak isn't stomped by a stale server copy.
        setAllPrefs(prev => {
          const merged = { ...v2 };
          for (const k of Object.keys(prev || {})) {
            merged[k] = { ...(v2[k] || {}), ...(prev[k] || {}) };
          }
          try { localStorage.setItem(STORAGE_KEY, JSON.stringify(merged)); } catch (_e) { /* noop */ }
          return merged;
        });
      } catch (e) {
        console.warn('[useMetricPreferences] hydrate failed:', e?.message || e);
      }
    })();
  }, []);

  const saveTimerRef = useRef(null);
  const scheduleBackendSave = useCallback((nextAllPrefs) => {
    const api = process.env.REACT_APP_BACKEND_URL;
    if (!api) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    // Debounce to coalesce rapid slot changes into a single PUT.
    saveTimerRef.current = setTimeout(async () => {
      try {
        await fetch(`${api}/api/users/preferences`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ monitor_metrics_v2: nextAllPrefs }),
        });
      } catch (e) {
        // Silent failure — localStorage remains authoritative, next login will retry hydrate
        console.warn('[useMetricPreferences] backend save failed:', e?.message || e);
      }
    }, 800);
  }, []);

  const setSlot = useCallback((slot, metricId) => {
    setAllPrefs(prev => {
      const next = {
        ...prev,
        [writeKey]: { ...(prev[writeKey] || {}), [slot]: metricId },
      };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch (_e) { /* noop */ }
      scheduleBackendSave(next);
      return next;
    });
  }, [writeKey, scheduleBackendSave]);

  return { preferences, setSlot };
}
